import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Server,
  Plus,
  Search,
  ArrowUpDown,
  MoreVertical,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface Instance {
  id: string;
  name: string;
  odoo_url: string;
  status: "active" | "inactive" | "error";
  health_status: "healthy" | "degraded" | "down";
  active_bobs: number;
  tier: string;
  created_at: string;
}

export function InstanceList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<keyof Instance>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Fetch instances
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-instances"],
    queryFn: () => api.getMyInstances(),
  });

  const instances = data?.instances || [];

  // Filter and sort instances
  const filteredInstances = instances
    .filter((instance: Instance) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        instance.name.toLowerCase().includes(searchLower) ||
        instance.odoo_url.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        statusFilter === "all" || instance.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a: Instance, b: Instance) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

  const handleSort = (field: keyof Instance) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getHealthIcon = (status: Instance["health_status"]) => {
    switch (status) {
      case "healthy":
        return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
      case "degraded":
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
      case "down":
        return <XCircle className="h-4 w-4 text-red-400" />;
    }
  };

  const getStatusBadgeVariant = (
    status: Instance["status"],
  ): "default" | "secondary" | "destructive" => {
    switch (status) {
      case "active":
        return "default";
      case "inactive":
        return "secondary";
      case "error":
        return "destructive";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-300">Loading instances...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="text-red-400">Error loading instances</div>
        <div className="text-sm text-slate-500">
          {(error as Error).message || "An error occurred"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header>
        <h1 className="text-3xl font-semibold text-slate-100">My Instances</h1>
        <p className="text-sm text-slate-300 mt-2">
          Manage your Odoo instances and BoB installations
        </p>
      </header>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1" role="search">
          <label htmlFor="instance-search" className="sr-only">
            Search instances
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300"
              aria-hidden="true"
            />
            <Input
              id="instance-search"
              type="search"
              placeholder="Search by name, domain, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              aria-label="Search instances"
            />
          </div>
        </div>

        {/* Filter dropdown */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className="w-full sm:w-[200px]"
            aria-label="Filter by status"
          >
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>

        {/* Create button */}
        <Button asChild>
          <Link to="/client/instances/create">
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            Create Instance
          </Link>
        </Button>
      </div>

      {/* Instances table - PROD-P0-004 Enhanced empty state */}
      {filteredInstances.length === 0 ? (
        <div className="space-y-6">
          <div className="text-center py-12 bg-slate-800/30 rounded-lg border border-slate-700">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 mb-4">
              <Server
                className="h-8 w-8 text-blue-400"
                aria-hidden="true"
              />
            </div>
            <h2 className="text-xl font-semibold text-slate-100 mb-2">
              {searchQuery || statusFilter !== "all"
                ? "No Instances Match Your Filters"
                : "No Instances Yet"}
            </h2>
            <p className="text-sm text-slate-300 mb-6 max-w-md mx-auto">
              {searchQuery || statusFilter !== "all"
                ? "Try adjusting your search terms or status filters to find instances."
                : "You haven't created any Odoo instances yet. Create your first instance to get started with AIIA."}
            </p>
            {!searchQuery && statusFilter === "all" ? (
              <Button asChild size="lg">
                <Link to="/client/instances/create">
                  <Plus className="h-5 w-5 mr-2" aria-hidden="true" />
                  Create Your First Instance
                </Link>
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                }}
                aria-label="Clear all filters"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Quick help for new users */}
          {!searchQuery && statusFilter === "all" && (
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-6">
              <h3 className="font-semibold text-slate-100 mb-3">💡 Quick Tip</h3>
              <p className="text-sm text-slate-300 mb-4">
                After creating an instance, you'll need to:
              </p>
              <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside">
                <li>Download the AIIA Connector installer</li>
                <li>Install the connector on your Odoo server</li>
                <li>Browse the BoB Marketplace to add functionality</li>
              </ol>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 bg-slate-800/30">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    className="flex items-center gap-2 hover:text-slate-300 transition-colors"
                    onClick={() => handleSort("name")}
                    aria-label={`Sort by name ${
                      sortField === "name" && sortDirection === "asc"
                        ? "descending"
                        : "ascending"
                    }`}
                  >
                    Instance Name
                    <ArrowUpDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Health</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>
                  <button
                    className="flex items-center gap-2 hover:text-slate-300 transition-colors"
                    onClick={() => handleSort("active_bobs")}
                    aria-label={`Sort by BoBs installed ${
                      sortField === "active_bobs" && sortDirection === "asc"
                        ? "descending"
                        : "ascending"
                    }`}
                  >
                    BoBs
                    <ArrowUpDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    className="flex items-center gap-2 hover:text-slate-300 transition-colors"
                    onClick={() => handleSort("created_at")}
                    aria-label={`Sort by created date ${
                      sortField === "created_at" && sortDirection === "asc"
                        ? "descending"
                        : "ascending"
                    }`}
                  >
                    Created
                    <ArrowUpDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                </TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInstances.map((instance: Instance) => (
                <TableRow
                  key={instance.id}
                  className="hover:bg-slate-700/30 cursor-pointer"
                  onClick={() => navigate(`/client/instances/${instance.id}`)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/client/instances/${instance.id}`);
                    }
                  }}
                  role="link"
                  aria-label={`View details for ${instance.name}`}
                >
                  <TableCell className="font-medium text-slate-200">
                    {instance.name}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={getStatusBadgeVariant(instance.status)}
                      className="capitalize"
                    >
                      {instance.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getHealthIcon(instance.health_status)}
                      <span className="text-sm capitalize text-slate-300">
                        {instance.health_status}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300">
                    {new URL(instance.odoo_url).hostname}
                  </TableCell>
                  <TableCell>
                    <span
                      className="text-slate-300"
                      aria-label={`${instance.active_bobs} BoBs installed`}
                    >
                      {instance.active_bobs}
                    </span>
                  </TableCell>
                  <TableCell>
                    <time
                      dateTime={instance.created_at}
                      className="text-slate-300"
                    >
                      {formatDate(instance.created_at)}
                    </time>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Actions for ${instance.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Open actions menu (future implementation)
                      }}
                    >
                      <MoreVertical
                        className="h-4 w-4"
                        aria-hidden="true"
                      />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Results count */}
      {filteredInstances.length > 0 && (
        <div className="text-sm text-slate-300" role="status" aria-live="polite">
          Showing {filteredInstances.length} of {instances.length} instance
          {instances.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}
